import { variables } from '@/config/variables';
import {
  Calendar,
  ChevronLeft,
  FileText,
  LayoutDashboard,
  LogOut,
  Settings,
  Shield,
  Users,
  Workflow,
} from 'lucide-react';
import { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuthStore } from '../../lib/stores';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const { user, logout } = useAuthStore();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    ...(variables.enableWorkflow
      ? [{ name: 'Workflows', href: '/workflows', icon: Workflow }]
      : []),
    { name: 'Schedules', href: '/schedules', icon: Calendar },
    { name: 'Executions', href: '/executions', icon: FileText },
    ...(user?.role === 'admin'
      ? [
          { name: 'Users', href: '/users', icon: Users },
          { name: 'Admin Settings', href: '/admin', icon: Shield },
        ]
      : []),
  ];

  // when user press Ctrl + B, toggle collapse
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'b') {
        onToggleCollapse();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onToggleCollapse]);

  return (
    <div
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-sidebar border-r border-gray-200 transition-all duration-300 shadow-lg',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
          {!collapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Workflow className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">JSONConverter</span>
            </div>
          )}

          <Tooltip>
            <TooltipTrigger>
              <Button variant="ghost" size="icon" onClick={onToggleCollapse}>
                <ChevronLeft
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    collapsed && 'rotate-180',
                  )}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{collapsed ? 'Expand' : 'Collapse'}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-2 py-4">
          {navigation.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200',
                    isActive
                      ? 'bg-primary text-[#faf7f4]'
                      : 'text-foreground hover:bg-primary/10 hover:text-gray-900',
                  )
                }
              >
                <Icon className={cn('flex-shrink-0 h-5 w-5', !collapsed && 'mr-3')} />
                {!collapsed && <span>{item.name}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* User Profile & Logout */}
        <div className="border-t border-gray-200 p-4">
          {user && (
            <div className="flex items-center space-x-2 mb-4 justify-between">
              <div className="flex items-center space-x-2">
                <div
                  className={cn(
                    'min-w-8 h-8 bg-primary rounded-full flex items-center justify-center',
                    collapsed && 'cursor-pointer text select-none',
                  )}
                >
                  <span className="text-sm font-medium text-white">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                {!collapsed && (
                  <div className="hidden md:block">
                    <p className="text-sm font-medium">{user?.username}</p>
                    <p className="text-xs text-foreground capitalize">{user?.role}</p>
                  </div>
                )}
              </div>
              {!collapsed && (
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="icon" asChild className="hover:bg-primary/10">
                    <Link to="/settings">
                      <Settings className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            onClick={logout}
            className={cn(
              'w-full px-2 justify-start text-foreground hover:text-red-600 hover:bg-red-50',
              collapsed && 'justify-center p-0',
            )}
          >
            <LogOut className={cn('h-4 w-4', !collapsed && 'mr-2')} />
            {!collapsed && 'Sign Out'}
          </Button>
        </div>
      </div>
    </div>
  );
}
